// The offline audio state an app renders from, and the one place passes start.
// Single-flight: a request during a pass queues one rerun with the latest
// input. Every platform dependency is injected, so the native app reuses it whole.

import type { AssetSource, AudioAsset } from "@aybuben/core/content/assets.ts";
import type { Course } from "@aybuben/core/content/course.ts";
import type { GameState } from "@aybuben/core/state.ts";
import { createStore, type StoreApi } from "zustand/vanilla";

import type { Capture } from "../analytics/events.ts";
import {
	audioWindow,
	orderedUnits,
	unitAudio,
	wantedAudio,
	withHint,
	type AudioWindow,
} from "./audio-sets.ts";
import { syncAudio, type SyncReport } from "./sync.ts";
import type { AudioStore, Download } from "./types.ts";

export interface OfflineAudioState {
	readonly storedKeys: ReadonlySet<string>;
	/** No connection: the platform says so, or the last pass stopped on the network. */
	readonly offline: boolean;
	/** When the launch pass's priority downloads settled; null until then. */
	readonly priorityDoneAt: number | null;
	readonly phase: "idle" | "priority" | "background";
}

export interface SyncRequest {
	readonly course: Course;
	readonly st: GameState;
	readonly assets: AssetSource;
	readonly lookaheadUnits: number;
	/** `st` folds the account's whole history. Until it does nothing is deleted, and `hint` steers the window. */
	readonly known: boolean;
	/** The window last computed from known history, for use while `known` is false. */
	readonly hint: AudioWindow | null;
}

export type Schedule = (fn: () => void, ms: number) => () => void;
export type Lock = <T>(fn: () => Promise<T>) => Promise<T>;

export interface OfflineAudioDeps {
	/** Null where nothing can be stored (no Cache Storage, unit tests): every pass is a no-op. */
	readonly store: AudioStore | null;
	readonly download: Download;
	readonly isOnline: () => boolean;
	readonly now: () => number;
	readonly capture?: Capture;
	/** Retry timers; injectable so tests need no real waiting. */
	readonly schedule?: Schedule;
	/** Serialises passes across tabs, which share one cache. */
	readonly lock?: Lock;
}

export interface OfflineAudio {
	readonly state: StoreApi<OfflineAudioState>;
	/** Starts a pass, or queues one if a pass is running. Resolves when no pass is left. */
	request(r: SyncRequest): Promise<void>;
	/** Whether every named recording is stored. An id the manifest cannot resolve counts as missing. */
	stored(assetIds: Iterable<string>, assets: AssetSource): boolean;
	/** Connectivity changed. Back online after being offline, the last request runs again. */
	setOnline(online: boolean): void;
	/** Re-reads what is stored: another tab may have changed it. */
	refresh(): Promise<void>;
	/** Resolves when no pass is running or queued. */
	whenIdle(): Promise<void>;
	/** Cancels a scheduled retry. */
	stop(): void;
}

/** Waits before retrying a pass the network or a struggling server cut short; the last repeats. */
export const RETRY_MS: readonly number[] = [15_000, 60_000, 300_000];

/**
 * Passes in a row in which one file, and only it, is lost to the network
 * before that file stops causing retries. One large file on a slow link would
 * otherwise be retried every few minutes for the whole session.
 */
export const GIVE_UP_PASSES = 3;

const defaultSchedule: Schedule = (fn, ms) => {
	const t = setTimeout(fn, ms);
	return () => clearTimeout(t);
};

export function createOfflineAudio(deps: OfflineAudioDeps): OfflineAudio {
	const schedule = deps.schedule ?? defaultSchedule;
	const lock: Lock = deps.lock ?? ((fn) => fn());
	const state = createStore<OfflineAudioState>()(() => ({
		storedKeys: new Set<string>(),
		offline: !deps.isOnline(),
		priorityDoneAt: deps.store ? null : deps.now(),
		phase: "idle",
	}));
	const releasePriority = () =>
		state.setState((s) => ({ priorityDoneAt: s.priorityDoneAt ?? deps.now() }));

	const resolve = (assets: AssetSource, ids: Iterable<string>): AudioAsset[] => {
		const out: AudioAsset[] = [];
		for (const id of ids) {
			try {
				out.push(assets.asset(id));
			} catch {
				// The content load already proved every name resolves; a miss means
				// the manifest and the bundle disagree. Skip it, never stop the pass.
				deps.capture?.("audio_asset_unresolved", { asset: id });
			}
		}
		return out;
	};

	// A given-up file is still in every pass's wanted set; it only stops
	// scheduling retries. A new launch (a new controller), course or window
	// tries it afresh.
	const giveUp: {
		course: Course | null;
		window: string | null;
		alone: string | null;
		passes: number;
		files: Set<string>;
	} = { course: null, window: null, alone: null, passes: 0, files: new Set() };

	const noteNetworkFailures = (course: Course, w: AudioWindow, report: SyncReport) => {
		const window = `${w.current}:${w.last}`;
		if (course !== giveUp.course || window !== giveUp.window) {
			giveUp.course = course;
			giveUp.window = window;
			giveUp.alone = null;
			giveUp.passes = 0;
			giveUp.files.clear();
		}
		// A pass the network ended, or one that lost several files, says the
		// link is bad, not that one file is stuck.
		const [only] = report.networkFailed;
		if (report.outcome !== "complete" || report.networkFailed.size !== 1 || only === undefined) {
			giveUp.alone = null;
			giveUp.passes = 0;
			return;
		}
		giveUp.passes = giveUp.alone === only ? giveUp.passes + 1 : 1;
		giveUp.alone = only;
		if (giveUp.passes >= GIVE_UP_PASSES) giveUp.files.add(only);
	};

	const pass = async (store: AudioStore, r: SyncRequest): Promise<SyncReport | null> => {
		const launch = state.getState().priorityDoneAt === null;
		const started = deps.now();
		const wasOffline = state.getState().offline;
		const timing: { priorityMs: number | null } = { priorityMs: null };
		state.setState({ phase: launch ? "priority" : "background" });
		try {
			const units = orderedUnits(r.course);
			const computed = audioWindow(units, r.st, r.lookaheadUnits);
			const w = r.known ? computed : withHint(computed, r.hint, units.length);
			const current = units[w.current];
			const report = await syncAudio({
				wanted: resolve(r.assets, wantedAudio(r.course, w)),
				priority: resolve(r.assets, current ? unitAudio(r.course, current) : []),
				store,
				download: deps.download,
				// The launch pass skips the lock, so it must never delete, whatever `known` says.
				allowCleanup: r.known && !launch,
				onStored: (k) =>
					state.setState((s) => ({ storedKeys: new Set(s.storedKeys).add(k), offline: false })),
				onPriorityDone: (stored) => {
					timing.priorityMs = deps.now() - started;
					state.setState({ storedKeys: new Set(stored), phase: "background" });
					releasePriority();
				},
				onFailed: (a, e) =>
					deps.capture?.("audio_download_failed", { file_id: a.fileId, kind: e.kind }),
			});
			noteNetworkFailures(r.course, w, report);
			// A pass with nothing to fetch proves nothing about the connection, so it
			// must not clear an offline flag a network failure set. A stored file does
			// prove it, and `onStored` has already cleared the flag.
			const stillOffline =
				report.outcome === "network" || !deps.isOnline() || (wasOffline && report.downloaded === 0);
			state.setState({ storedKeys: new Set(report.stored), offline: stillOffline });
			deps.capture?.("audio_sync_finished", {
				phase: launch ? "launch" : "background",
				outcome: report.outcome,
				downloaded: report.downloaded,
				failed: report.failed,
				bytes: report.bytes,
				ms: deps.now() - started,
				priority_ms: timing.priorityMs,
			});
			return report;
		} catch (e) {
			// Storage that throws (a privacy mode, blocked site data) must not hold
			// the launch screen up: the learner gets the app, and lessons stream online.
			releasePriority();
			deps.capture?.("audio_sync_failed", { message: String(e) });
			return null;
		}
	};

	let running: Promise<void> | null = null;
	// Objects, not `let`s: they change inside closures TypeScript cannot see.
	const pending: { next: SyncRequest | null; last: SyncRequest | null } = {
		next: null,
		last: null,
	};
	const retry: { attempt: number; cancel: (() => void) | null } = { attempt: 0, cancel: null };

	const clearRetry = () => {
		retry.cancel?.();
		retry.cancel = null;
	};

	const afterPasses = (report: SyncReport | null) => {
		// Full storage is not retried: waiting does not free space.
		const cutShort =
			report !== null &&
			report.outcome !== "storage" &&
			(report.outcome === "network" ||
				report.outcome === "server" ||
				[...report.networkFailed].some((k) => !giveUp.files.has(k)));
		if (!cutShort) {
			retry.attempt = 0;
			return;
		}
		// Offline per the platform, the `online` event brings the next pass; a timer would only fail again.
		if (!deps.isOnline()) return;
		// `stop()` cleared the request: nothing is left to retry.
		if (!pending.last) return;
		const wait = RETRY_MS[Math.min(retry.attempt, RETRY_MS.length - 1)]!; // index clamped into the array
		retry.attempt++;
		clearRetry();
		retry.cancel = schedule(() => {
			retry.cancel = null;
			if (pending.last) void api.request(pending.last);
		}, wait);
	};

	const api: OfflineAudio = {
		state,
		request(r) {
			pending.last = r;
			const store = deps.store;
			if (!store) return Promise.resolve();
			clearRetry();
			if (running) {
				pending.next = r;
				return running;
			}
			// Set before any await, so a watcher never sees "idle" between a request and its pass.
			state.setState({
				phase: state.getState().priorityDoneAt === null ? "priority" : "background",
			});
			running = (async () => {
				let report: SyncReport | null = null;
				try {
					let next: SyncRequest | null = r;
					while (next) {
						const current: SyncRequest = next;
						pending.next = null;
						// The launch pass skips the lock: queued behind another tab's whole
						// background pass, it would hold the launch screen until maxWaitMs.
						// Its writes are idempotent, and it runs before the history is
						// known, so it deletes nothing.
						const launch = state.getState().priorityDoneAt === null;
						try {
							report = launch ? await pass(store, current) : await lock(() => pass(store, current));
						} catch (e) {
							// A lock that cannot be taken (Web Locks throwing) must not hold the launch up either.
							releasePriority();
							deps.capture?.("audio_sync_failed", { message: String(e) });
							report = null;
						}
						next = pending.next;
					}
				} finally {
					running = null;
					state.setState({ phase: "idle" });
				}
				afterPasses(report);
			})();
			return running;
		},
		stored(assetIds, assets) {
			const store = deps.store;
			if (!store) return false;
			const keys = state.getState().storedKeys;
			for (const id of assetIds) {
				let a: AudioAsset;
				try {
					a = assets.asset(id);
				} catch {
					return false;
				}
				if (!keys.has(store.key(a))) return false;
			}
			return true;
		},
		setOnline(online) {
			const was = state.getState().offline;
			state.setState({ offline: !online });
			if (online && was && pending.last) void api.request(pending.last);
		},
		async refresh() {
			const store = deps.store;
			// A running pass writes `storedKeys` itself; reading mid-pass could drop a key it just added.
			if (!store || running) return;
			// Callers fire this from a visibilitychange handler with `void`, so a rejection would go unhandled.
			const keys = await store.keys().catch(() => null);
			if (!keys) return;
			// Checked again: a pass may have started, and stored keys, during the read.
			if (!running) state.setState({ storedKeys: keys });
		},
		whenIdle() {
			return running ?? Promise.resolve();
		},
		stop() {
			clearRetry();
			pending.next = null;
			pending.last = null;
			// The next request may be another account on the same course and window.
			giveUp.course = null;
			giveUp.window = null;
		},
	};
	return api;
}
